import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Loader2, ShieldAlert, BarChart3, Inbox, Building2, Users, FileImage,
    Sparkles, TrendingUp, Trophy, Activity,
} from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Cell,
    AreaChart, Area, Legend, PieChart, Pie, LabelList,
} from "recharts";

// Labels lisibles pour les intérêts CEE
const CEE_INTEREST_LABELS = {
    isolation: "Isolation",
    pompe_chaleur: "Pompe à chaleur",
    led: "Éclairage LED",
    ventilation: "Ventilation",
    autoconsommation: "Autoconsommation",
    audit: "Audit énergétique",
};
const AUDIENCE_COLORS = ["#0A3B2A", "#C7F462"];
const ENERGY_COLORS = ["#0A3B2A", "#DDEFE7"];

const QUOTE_STATUS_COLORS = {
    new: "#3b82f6",
    contacted: "#f59e0b",
    quote_sent: "#8b5cf6",
    won: "#10b981",
    lost: "#ef4444",
};

// Stable chart styles — defined at module scope to avoid creating new object refs on every render.
const CHART_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid hsl(152, 9%, 85%)" };
const LEGEND_WRAPPER_STYLE = { fontSize: 12 };
const BAR_RADIUS = [8, 8, 0, 0];
const QUOTE_STATUS_LABELS = {
    new: "Nouveau",
    contacted: "Contacté",
    quote_sent: "Devis envoyé",
    won: "Gagné",
    lost: "Perdu",
};

function KPICard({ icon: Icon, label, value, hint, highlight }) {
    return (
        <Card className={`rounded-2xl border-border ${highlight ? "ring-2 ring-primary" : ""}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center">
                        <Icon className="h-5 w-5 text-accent-foreground" />
                    </div>
                    <p className="em-eyebrow">{label}</p>
                </div>
                <p className="font-display text-3xl font-black text-primary">{value}</p>
                {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
            </CardContent>
        </Card>
    );
}

export default function AdminAnalyticsGlobal() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/admin/analytics/global", { params: { period } });
            setData(d);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, period]);

    if (authLoading) return <div className="em-container py-32 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20" data-testid="admin-forbidden">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90"><Link to="/">Retour à l'accueil</Link></Button>
            </div>
        );
    }

    return (
        <div className="em-container py-8" data-testid="admin-analytics-global-page">
            <p className="em-eyebrow flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" />Vue d'ensemble</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Tableau de bord global</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Indicateurs clés du business Energy Market : conversion, leads, moteur, utilisateurs.</p>

            <div className="mt-6">
                <Tabs value={period} onValueChange={setPeriod}>
                    <TabsList className="rounded-full" data-testid="period-tabs">
                        <TabsTrigger value="7d" className="rounded-full" data-testid="period-7d">7 jours</TabsTrigger>
                        <TabsTrigger value="30d" className="rounded-full" data-testid="period-30d">30 jours</TabsTrigger>
                        <TabsTrigger value="90d" className="rounded-full" data-testid="period-90d">90 jours</TabsTrigger>
                        <TabsTrigger value="all" className="rounded-full" data-testid="period-all">Tout</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {loading || !data ? (
                <div className="py-20 text-center text-muted-foreground" data-testid="analytics-loading">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                </div>
            ) : (
                <>
                    {/* CONVERSION HIGHLIGHT */}
                    <Card className="rounded-3xl bg-primary text-primary-foreground border-0 mt-10" data-testid="conversion-card">
                        <CardContent className="p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                            <div className="flex items-center gap-5">
                                <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center">
                                    <Trophy className="h-7 w-7 text-accent-foreground" />
                                </div>
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-accent">Taux de conversion devis</p>
                                    <p className="font-display text-5xl font-black mt-1">{data.quotes.conversion_rate}%</p>
                                    <p className="text-sm text-primary-foreground/70">
                                        {data.quotes.won} gagné · {data.quotes.closed} clôturé
                                    </p>
                                </div>
                            </div>
                            <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="cta-quotes">
                                <Link to="/admin/devis">
                                    Voir tous les devis →
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>

                    {/* KPI ROW */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="kpi-row">
                        <KPICard icon={Inbox} label="Devis reçus" value={data.quotes.total} hint={`${data.quotes.won} gagnés`} />
                        <KPICard icon={Building2} label="Leads CEE/Solaire" value={data.cee_leads.total} hint={`${data.cee_leads.won} gagnés`} />
                        <KPICard icon={Users} label="Utilisateurs" value={data.users.total} hint={`${data.users.b2c} B2C · ${data.users.b2b} B2B`} />
                        <KPICard icon={Sparkles} label="Quiz exécutés" value={data.quiz.total} hint="recommandations générées" />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4" data-testid="kpi-row-2">
                        <KPICard icon={Activity} label="Mesures de conso" value={data.consumption.total_records} hint={`${data.consumption.active_users} users actifs`} />
                        <KPICard icon={FileImage} label="Factures analysées" value={data.bill_analysis.total} hint={`${data.bill_analysis.success_rate}% de succès OCR`} highlight={data.bill_analysis.total > 0} />
                        <KPICard icon={TrendingUp} label="Devis en cours" value={(data.quotes.by_status.new || 0) + (data.quotes.by_status.contacted || 0) + (data.quotes.by_status.quote_sent || 0)} hint="à traiter ou en suivi" />
                        <KPICard icon={Trophy} label="Total business" value={data.quotes.won + data.cee_leads.won} hint="devis + leads gagnés" />
                    </div>

                    {/* QUOTE FUNNEL */}
                    <Card className="rounded-3xl border-border mt-6" data-testid="quote-funnel">
                        <CardContent className="p-7">
                            <p className="em-eyebrow">Funnel des devis</p>
                            <h3 className="font-display text-xl font-bold text-primary mt-1 mb-5">Pipeline commercial</h3>
                            <div className="h-[280px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={Object.entries(data.quotes.by_status).map(([k, v]) => ({
                                            name: QUOTE_STATUS_LABELS[k] || k,
                                            value: v,
                                            color: QUOTE_STATUS_COLORS[k] || "#999",
                                        }))}
                                        margin={CHART_MARGIN}
                                    >
                                        <CartesianGrid stroke="hsl(152, 9%, 90%)" strokeDasharray="3 3" />
                                        <XAxis dataKey="name" stroke="hsl(162, 7%, 38%)" fontSize={12} />
                                        <YAxis stroke="hsl(162, 7%, 38%)" fontSize={12} allowDecimals={false} />
                                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                                        <Bar dataKey="value" radius={BAR_RADIUS}>
                                            {Object.entries(data.quotes.by_status).map(([k]) => (
                                                <Cell key={`status-${k}`} fill={QUOTE_STATUS_COLORS[k] || "#0A3B2A"} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* TIME SERIES */}
                    <Card className="rounded-3xl border-border mt-6" data-testid="time-series">
                        <CardContent className="p-7">
                            <p className="em-eyebrow">Évolution hebdomadaire</p>
                            <h3 className="font-display text-xl font-bold text-primary mt-1 mb-5">Devis & leads CEE par semaine (12 dernières)</h3>
                            <div className="h-[300px]" data-testid="weekly-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={data.weekly_series || []}
                                        margin={CHART_MARGIN}
                                    >
                                        <defs>
                                            <linearGradient id="qGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="hsl(154, 71%, 14%)" stopOpacity={0.55} />
                                                <stop offset="100%" stopColor="hsl(154, 71%, 14%)" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="hsl(76, 86%, 60%)" stopOpacity={0.65} />
                                                <stop offset="100%" stopColor="hsl(76, 86%, 60%)" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="hsl(152, 9%, 90%)" strokeDasharray="3 3" />
                                        <XAxis dataKey="label" stroke="hsl(162, 7%, 38%)" fontSize={12} />
                                        <YAxis stroke="hsl(162, 7%, 38%)" fontSize={12} allowDecimals={false} />
                                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                                        <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER_STYLE} />
                                        <Area
                                            type="monotone"
                                            dataKey="quotes"
                                            name="Devis"
                                            stroke="hsl(154, 71%, 14%)"
                                            strokeWidth={2.5}
                                            fill="url(#qGrad)"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="cee_leads"
                                            name="Leads CEE/Solaire"
                                            stroke="hsl(154, 50%, 35%)"
                                            strokeWidth={2.5}
                                            fill="url(#cGrad)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* ── Nouveaux graphes : breakdowns ──────────────────────── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                        {/* PieChart : devis par audience */}
                        <Card className="rounded-3xl border-border" data-testid="chart-audience">
                            <CardContent className="p-7">
                                <p className="em-eyebrow">Répartition</p>
                                <h3 className="font-display text-xl font-bold text-primary mt-1 mb-5">Devis par audience</h3>
                                <div className="h-[260px]">
                                    {(data.quotes.by_audience?.b2c || 0) + (data.quotes.by_audience?.b2b || 0) === 0 ? (
                                        <EmptyChart text="Pas encore de devis" />
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[
                                                        { name: "Particuliers", value: data.quotes.by_audience?.b2c || 0 },
                                                        { name: "Entreprises",   value: data.quotes.by_audience?.b2b || 0 },
                                                    ]}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    innerRadius={55}
                                                    outerRadius={95}
                                                    paddingAngle={3}
                                                >
                                                    {AUDIENCE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                                                    <LabelList dataKey="value" position="outside" fill="hsl(162, 7%, 38%)" fontSize={12} />
                                                </Pie>
                                                <Tooltip contentStyle={TOOLTIP_STYLE} />
                                                <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER_STYLE} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* PieChart : devis par énergie */}
                        <Card className="rounded-3xl border-border" data-testid="chart-energy">
                            <CardContent className="p-7">
                                <p className="em-eyebrow">Répartition</p>
                                <h3 className="font-display text-xl font-bold text-primary mt-1 mb-5">Devis par énergie</h3>
                                <div className="h-[260px]">
                                    {(data.quotes.by_energy?.electricity || 0) + (data.quotes.by_energy?.gas || 0) === 0 ? (
                                        <EmptyChart text="Pas encore de devis" />
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[
                                                        { name: "Électricité", value: data.quotes.by_energy?.electricity || 0 },
                                                        { name: "Gaz",         value: data.quotes.by_energy?.gas || 0 },
                                                    ]}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    innerRadius={55}
                                                    outerRadius={95}
                                                    paddingAngle={3}
                                                >
                                                    {ENERGY_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                                                    <LabelList dataKey="value" position="outside" fill="hsl(162, 7%, 38%)" fontSize={12} />
                                                </Pie>
                                                <Tooltip contentStyle={TOOLTIP_STYLE} />
                                                <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER_STYLE} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                        {/* BarChart horizontal : intérêts CEE */}
                        <Card className="rounded-3xl border-border" data-testid="chart-cee-interests">
                            <CardContent className="p-7">
                                <p className="em-eyebrow">CEE & Solaire</p>
                                <h3 className="font-display text-xl font-bold text-primary mt-1 mb-5">Intérêts les plus demandés</h3>
                                <div className="h-[300px]">
                                    {Object.keys(data.cee_leads.by_interest || {}).length === 0 ? (
                                        <EmptyChart text="Aucun lead CEE pour l'instant" />
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={Object.entries(data.cee_leads.by_interest).map(([k, v]) => ({
                                                    name: CEE_INTEREST_LABELS[k] || k,
                                                    value: v,
                                                }))}
                                                layout="vertical"
                                                margin={{ top: 5, right: 25, left: 30, bottom: 5 }}
                                            >
                                                <CartesianGrid stroke="hsl(152, 9%, 90%)" strokeDasharray="3 3" />
                                                <XAxis type="number" stroke="hsl(162, 7%, 38%)" fontSize={12} allowDecimals={false} />
                                                <YAxis type="category" dataKey="name" stroke="hsl(162, 7%, 38%)" fontSize={12} width={140} />
                                                <Tooltip contentStyle={TOOLTIP_STYLE} />
                                                <Bar dataKey="value" fill="hsl(154, 71%, 14%)" radius={[0, 8, 8, 0]}>
                                                    <LabelList dataKey="value" position="right" fill="hsl(162, 7%, 38%)" fontSize={12} />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* BarChart horizontal : top fournisseurs recommandés */}
                        <Card className="rounded-3xl border-border" data-testid="chart-top-suppliers">
                            <CardContent className="p-7">
                                <p className="em-eyebrow">Moteur</p>
                                <h3 className="font-display text-xl font-bold text-primary mt-1 mb-5">Top fournisseurs recommandés</h3>
                                <div className="h-[300px]">
                                    {(data.quiz.top_suppliers || []).length === 0 ? (
                                        <EmptyChart text="Pas encore de recommandations" />
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={(data.quiz.top_suppliers || []).map((s) => ({
                                                    name: s.supplier_id,
                                                    value: s.count,
                                                }))}
                                                layout="vertical"
                                                margin={{ top: 5, right: 25, left: 30, bottom: 5 }}
                                            >
                                                <CartesianGrid stroke="hsl(152, 9%, 90%)" strokeDasharray="3 3" />
                                                <XAxis type="number" stroke="hsl(162, 7%, 38%)" fontSize={12} allowDecimals={false} />
                                                <YAxis type="category" dataKey="name" stroke="hsl(162, 7%, 38%)" fontSize={12} width={140} />
                                                <Tooltip contentStyle={TOOLTIP_STYLE} />
                                                <Bar dataKey="value" fill="hsl(76, 86%, 60%)" radius={[0, 8, 8, 0]}>
                                                    <LabelList dataKey="value" position="right" fill="hsl(162, 7%, 38%)" fontSize={12} />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
}


function EmptyChart({ text }) {
    return (
        <div className="h-full flex items-center justify-center text-sm text-muted-foreground italic">
            {text}
        </div>
    );
}
